import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  Briefcase, 
  FileText, 
  MessageSquare, 
  TrendingUp,
  Clock,
  CheckCircle,
  ArrowRight,
  Loader2,
  Building2,
  Users,
  PlusCircle,
  Target,
  Zap,
  Award,
  Globe
} from 'lucide-react';
import { getDashboardStats, getMatchingJobs, getApplicantProfile, getEmployerJobs } from '../services/api';

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [matchingJobs, setMatchingJobs] = useState([]);
  const [profile, setProfile] = useState(null);
  const [employerJobs, setEmployerJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes] = await Promise.all([getDashboardStats()]);
        setStats(statsRes.data);

        if (user?.user_type === 'applicant') {
          const [matchRes, profileRes] = await Promise.all([
            getMatchingJobs(),
            getApplicantProfile()
          ]);
          setMatchingJobs(matchRes.data.slice(0, 5));
          setProfile(profileRes.data);
        } else if (user?.user_type === 'employer') {
          const jobsRes = await getEmployerJobs();
          setEmployerJobs(jobsRes.data.slice(0, 5));
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const getMatchCategoryColor = (category) => {
    switch (category) {
      case 'excellent': return 'bg-green-100 text-green-700 border-green-200';
      case 'good': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'possible': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getMatchCategoryLabel = (category) => {
    switch (category) {
      case 'excellent': return 'Exzellent';
      case 'good': return 'Gut';
      case 'possible': return 'Möglich';
      default: return 'Entwicklung nötig';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  // Applicant Dashboard
  if (user?.user_type === 'applicant') {
    return (
      <Layout>
        <div className="space-y-8" data-testid="applicant-dashboard">
          {/* Header */}
          <div>
            <h1 className="text-3xl font-heading font-bold text-foreground">
              Willkommen, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-muted-foreground mt-1">
              Ihr Kompetenz-Dashboard
            </p>
          </div>

          {/* Profile Completion */}
          {profile && profile.profile_complete < 100 && (
            <Card className="border-primary/20 bg-primary/5">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-foreground">Profil vervollständigen</h3>
                    <p className="text-sm text-muted-foreground">
                      Ein vollständiges Profil verbessert Ihren Match-Score
                    </p>
                  </div>
                  <span className="text-2xl font-bold text-primary">{profile.profile_complete}%</span>
                </div>
                <Progress value={profile.profile_complete} className="h-2 mb-4" />
                <Link to="/profile">
                  <Button variant="outline" size="sm" className="rounded-full" data-testid="complete-profile-btn">
                    Profil bearbeiten
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <Target className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Hohe Matches</p>
                    <p className="text-2xl font-bold">{stats?.high_match_jobs || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Bewerbungen</p>
                    <p className="text-2xl font-bold">{stats?.total_applications || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <Award className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Kompetenzen</p>
                    <p className="text-2xl font-bold">{stats?.competency_count || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-yellow-100 rounded-lg">
                    <Clock className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Interviews</p>
                    <p className="text-2xl font-bold">{stats?.interviews || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Matching Jobs with Detailed Scores */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-heading font-semibold">Ihre besten Matches</h2>
              <Link to="/jobs">
                <Button variant="ghost" size="sm" className="text-primary">
                  Alle ansehen
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>

            {matchingJobs.length > 0 ? (
              <div className="grid gap-4">
                {matchingJobs.map((job) => (
                  <Link key={job.job_id} to={`/jobs/${job.job_id}`}>
                    <Card className="hover:shadow-md hover:border-primary/20 transition-all cursor-pointer">
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-foreground">{job.job_title}</h3>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getMatchCategoryColor(job.match_category)}`}>
                                {getMatchCategoryLabel(job.match_category)}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground">{job.company_name}</p>
                            <p className="text-sm text-muted-foreground mt-1">
                              {job.location} {job.work_model && `• ${job.work_model}`}
                            </p>
                            
                            {/* Match Reasons */}
                            <div className="flex flex-wrap gap-2 mt-3">
                              {job.match_reasons?.map((reason, idx) => (
                                <span 
                                  key={idx} 
                                  className="text-xs px-2 py-1 bg-green-50 text-green-700 rounded-full flex items-center gap-1"
                                >
                                  <CheckCircle className="w-3 h-3" />
                                  {reason}
                                </span>
                              ))}
                            </div>

                            {/* Development Suggestions */}
                            {job.development_suggestions?.length > 0 && (
                              <div className="flex flex-wrap gap-2 mt-2">
                                {job.development_suggestions?.slice(0, 2).map((suggestion, idx) => (
                                  <span 
                                    key={idx} 
                                    className="text-xs px-2 py-1 bg-yellow-50 text-yellow-700 rounded-full flex items-center gap-1"
                                  >
                                    <Zap className="w-3 h-3" />
                                    {suggestion}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Match Score Circle */}
                          <div className="text-center ml-4">
                            <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${
                              job.match_score >= 80 ? 'bg-green-100' :
                              job.match_score >= 60 ? 'bg-blue-100' :
                              job.match_score >= 40 ? 'bg-yellow-100' : 'bg-gray-100'
                            }`}>
                              <span className={`text-xl font-bold ${
                                job.match_score >= 80 ? 'text-green-700' :
                                job.match_score >= 60 ? 'text-blue-700' :
                                job.match_score >= 40 ? 'text-yellow-700' : 'text-gray-700'
                              }`}>
                                {job.match_score}%
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">Match</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="pt-6 text-center py-12">
                  <Target className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">
                    Vervollständigen Sie Ihr Profil mit Kompetenzen, um passende Jobs zu finden
                  </p>
                  <Link to="/profile">
                    <Button className="mt-4 rounded-full">Profil bearbeiten</Button>
                  </Link>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Quick Actions */}
          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/documents">
              <Card className="hover:shadow-md hover:border-primary/20 transition-all cursor-pointer h-full">
                <CardContent className="pt-6 text-center">
                  <FileText className="w-10 h-10 mx-auto text-primary mb-3" />
                  <h3 className="font-semibold">Lebenslauf erstellen</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    KI-generierter kompetenzbasierter CV
                  </p>
                </CardContent>
              </Card>
            </Link>

            <Link to="/jobs">
              <Card className="hover:shadow-md hover:border-primary/20 transition-all cursor-pointer h-full">
                <CardContent className="pt-6 text-center">
                  <Briefcase className="w-10 h-10 mx-auto text-primary mb-3" />
                  <h3 className="font-semibold">Jobs durchsuchen</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Finden Sie Ihren passenden Match
                  </p>
                </CardContent>
              </Card>
            </Link>

            <Link to="/messages">
              <Card className="hover:shadow-md hover:border-primary/20 transition-all cursor-pointer h-full">
                <CardContent className="pt-6 text-center">
                  <MessageSquare className="w-10 h-10 mx-auto text-primary mb-3" />
                  <h3 className="font-semibold">Nachrichten</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Kommunizieren Sie mit Arbeitgebern
                  </p>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  // Employer Dashboard
  return (
    <Layout>
      <div className="space-y-8" data-testid="employer-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold text-foreground">
              Arbeitgeber Dashboard
            </h1>
            <p className="text-muted-foreground mt-1">
              Kompetenzbasierte Kandidatensuche
            </p>
          </div>
          <Link to="/employer/jobs/new">
            <Button className="rounded-full gap-2" data-testid="create-job-btn">
              <PlusCircle className="w-4 h-4" />
              Neue Stelle
            </Button>
          </Link>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-primary/10 rounded-lg">
                  <Briefcase className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Aktive Jobs</p>
                  <p className="text-2xl font-bold">{stats?.active_jobs || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Bewerbungen</p>
                  <p className="text-2xl font-bold">{stats?.total_applications || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-lg">
                  <Target className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Hohe Matches</p>
                  <p className="text-2xl font-bold">{stats?.high_match_candidates || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-yellow-100 rounded-lg">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Neue</p>
                  <p className="text-2xl font-bold">{stats?.new_applications || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Building2 className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Gesamt Jobs</p>
                  <p className="text-2xl font-bold">{stats?.total_jobs || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Jobs */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-heading font-semibold">Ihre Stellenanzeigen</h2>
            <Link to="/employer/jobs">
              <Button variant="ghost" size="sm" className="text-primary">
                Alle ansehen
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>

          {employerJobs.length > 0 ? (
            <div className="grid gap-4">
              {employerJobs.map((job) => (
                <Link key={job.job_id} to={`/employer/jobs/${job.job_id}`}>
                  <Card className="hover:shadow-md hover:border-primary/20 transition-all cursor-pointer">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-foreground">{job.title}</h3>
                          <p className="text-sm text-muted-foreground">
                            {job.location} • {job.job_type} • {job.work_model || 'Vor Ort'}
                          </p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          job.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {job.status === 'active' ? 'Aktiv' : 'Inaktiv'}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="pt-6 text-center py-12">
                <Briefcase className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  Sie haben noch keine Stellenanzeigen erstellt
                </p>
                <Link to="/employer/jobs/new">
                  <Button className="mt-4 rounded-full">Erste Stelle erstellen</Button>
                </Link>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
